/**
 * JobRole API Contract
 *
 * Mirrors: backend/src/routes/jobRole.routes.ts
 * 1:1 parity with existing Express routes
 */
import { oc } from "@orpc/contract";
import { z } from "zod";
import { uuidSchema } from "@hourly/api/validation-common";
import { JobRoleResponseSchema, CreateJobRoleInputSchema, UpdateJobRoleInputSchema, UpdateJobRoleSkillsInputSchema, AddJobRoleSkillInputSchema, ArchiveJobRoleInputSchema, } from "@hourly/api/jobRole";
// ============================================
// JobRole Contracts
// ============================================
/**
 * POST /api/job-roles
 * Create a new job role
 */
export const createJobRole = oc
    .route({
    method: "POST",
    path: "/job-roles",
    summary: "Create a new job role",
    tags: ["JobRoles"],
    successStatus: 201,
})
    .input(CreateJobRoleInputSchema.omit({ companyId: true }))
    .output(JobRoleResponseSchema);
/**
 * GET /api/job-roles/:id
 * Get a job role by ID
 */
export const getJobRole = oc
    .route({
    method: "GET",
    path: "/job-roles/{id}",
    summary: "Get job role by ID",
    tags: ["JobRoles"],
})
    .input(z.object({ id: uuidSchema }))
    .output(JobRoleResponseSchema);
/**
 * GET /api/job-roles
 * Get all job roles for the authenticated user's company
 */
export const getJobRolesByCompany = oc
    .route({
    method: "GET",
    path: "/job-roles",
    summary: "List job roles for a company",
    tags: ["JobRoles"],
})
    .input(z.object({
    includeArchived: z
        .string()
        .optional()
        .transform((val) => val === "true"),
}))
    .output(z.array(JobRoleResponseSchema));
/**
 * GET /api/job-role-families/:familyId/job-roles
 * Get all job roles for a job role family
 */
export const getJobRolesByFamily = oc
    .route({
    method: "GET",
    path: "/job-role-families/{familyId}/job-roles",
    summary: "List job roles for a job role family",
    tags: ["JobRoles"],
})
    .input(z.object({
    familyId: uuidSchema,
    includeArchived: z
        .string()
        .optional()
        .transform((val) => val === "true"),
}))
    .output(z.array(JobRoleResponseSchema));
/**
 * PATCH /api/job-roles/:id
 * Update a job role
 */
export const updateJobRole = oc
    .route({
    method: "PATCH",
    path: "/job-roles/{id}",
    summary: "Update a job role",
    tags: ["JobRoles"],
})
    .input(z.object({ id: uuidSchema }).merge(UpdateJobRoleInputSchema))
    .output(JobRoleResponseSchema);
/**
 * DELETE /api/job-roles/:id
 * Delete a job role
 */
export const deleteJobRole = oc
    .route({
    method: "DELETE",
    path: "/job-roles/{id}",
    summary: "Delete a job role",
    tags: ["JobRoles"],
    successStatus: 204,
})
    .input(z.object({ id: uuidSchema }))
    .output(z.void());
/**
 * PATCH /api/job-roles/:id/skills
 * Update skills array for a job role
 */
export const updateJobRoleSkills = oc
    .route({
    method: "PATCH",
    path: "/job-roles/{id}/skills",
    summary: "Update skills array for a job role",
    tags: ["JobRoles"],
})
    .input(z.object({ id: uuidSchema }).merge(UpdateJobRoleSkillsInputSchema))
    .output(JobRoleResponseSchema);
/**
 * POST /api/job-roles/:id/skills
 * Add a single skill to a job role
 */
export const addJobRoleSkill = oc
    .route({
    method: "POST",
    path: "/job-roles/{id}/skills",
    summary: "Add a single skill to a job role",
    tags: ["JobRoles"],
})
    .input(z.object({ id: uuidSchema }).merge(AddJobRoleSkillInputSchema))
    .output(JobRoleResponseSchema);
/**
 * DELETE /api/job-roles/:id/skills/:skillId
 * Remove a skill from a job role
 */
export const removeJobRoleSkill = oc
    .route({
    method: "DELETE",
    path: "/job-roles/{id}/skills/{skillId}",
    summary: "Remove a skill from a job role",
    tags: ["JobRoles"],
})
    .input(z.object({
    id: uuidSchema,
    skillId: uuidSchema,
}))
    .output(JobRoleResponseSchema);
/**
 * POST /api/job-roles/:id/archive
 * Archive a job role
 */
export const archiveJobRole = oc
    .route({
    method: "POST",
    path: "/job-roles/{id}/archive",
    summary: "Archive a job role",
    tags: ["JobRoles"],
})
    .input(z.object({ id: uuidSchema }).merge(ArchiveJobRoleInputSchema))
    .output(JobRoleResponseSchema);
/**
 * POST /api/job-roles/:id/unarchive
 * Unarchive a job role
 */
export const unarchiveJobRole = oc
    .route({
    method: "POST",
    path: "/job-roles/{id}/unarchive",
    summary: "Unarchive a job role",
    tags: ["JobRoles"],
})
    .input(z.object({ id: uuidSchema }).merge(ArchiveJobRoleInputSchema))
    .output(JobRoleResponseSchema);
// ============================================
// Contract Router
// ============================================
export const jobRoleContract = {
    create: createJobRole,
    get: getJobRole,
    getByCompany: getJobRolesByCompany,
    getByFamily: getJobRolesByFamily,
    update: updateJobRole,
    delete: deleteJobRole,
    updateSkills: updateJobRoleSkills,
    addSkill: addJobRoleSkill,
    removeSkill: removeJobRoleSkill,
    archive: archiveJobRole,
    unarchive: unarchiveJobRole,
};
//# sourceMappingURL=jobRole.js.map